import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import type { User, Session } from '@supabase/supabase-js'
import { supabase } from './supabase'
import { classifyValidation } from './sessionBootstrap'
import { deviceTimeZone } from './dates'
import { disableAlerts, forgetThisDevice } from '@/lib/pushAlerts'

/**
 * How long a sign-out waits for this device's push alerts to go off before it signs out anyway (RIZ132), the
 * backstop over disableAlerts' own limits: it waits for the browser's unsubscribe at most UNSUBSCRIBE_TIMEOUT_MS
 * and then for its delete at most DELETE_TIMEOUT_MS (3 s each), so either one hanging alone ends inside this,
 * and both hanging end here. Nothing about push may hold a sign-out up.
 */
const SIGN_OUT_ALERTS_LIMIT_MS = 4_000

interface Membership {
  id: string
  organization_id: string
  role: string
  is_default: boolean
  organizations: {
    id: string
    name: string
    slug: string
    plan: string
    brand: Record<string, unknown>
  }
}

// Current user's profile fields the shared chrome (sidebar) renders. Sourced
// here so an avatar/name change can refresh app-wide via refreshProfile().
interface UserProfile {
  avatar_url: string | null
  full_name: string | null
  first_name: string | null
  last_name: string | null
  // Per-user UI preferences (F3: contact_card_order). jsonb; defaults to {}.
  preferences: Record<string, unknown> | null
  // IANA display timezone (PR-2): read via useUserTimeZone(), edited on
  // /profile (which calls refreshProfile() on save, so a change propagates
  // app-wide without a reload).
  timezone: string | null
}

interface AuthContextType {
  user: User | null
  session: Session | null
  memberships: Membership[]
  membershipsLoaded: boolean
  activeOrg: Membership | null
  setActiveOrg: (org: Membership) => void
  profile: UserProfile | null
  /** Resolves true when `profile` now reflects a fresh read; false = the fetch
   *  failed and the last-good profile was kept (see fetchProfile). */
  refreshProfile: () => Promise<boolean>
  loading: boolean
  signUp: (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    terms: { version: string; hash: string },
  ) => Promise<{ error: Error | null }>
  signIn: (email: string, password: string) => Promise<{ error: Error | null; data: { user: User | null; session: Session | null } }>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

// The chosen org persists per-user so a refresh (or re-login) doesn't bounce a
// multi-org user back to their default org mid-session.
const activeOrgStorageKey = (userId: string) => `rizeos.active-org.${userId}`

function readStoredOrgId(userId: string): string | null {
  try { return localStorage.getItem(activeOrgStorageKey(userId)) } catch { return null }
}

function writeStoredOrgId(userId: string, orgId: string) {
  try { localStorage.setItem(activeOrgStorageKey(userId), orgId) } catch { /* storage unavailable — the choice just won't persist */ }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [memberships, setMemberships] = useState<Membership[]>([])
  // Distinguishes "memberships have been fetched for the current user" from the
  // initial empty array. ProtectedRoute must wait for this before treating a user
  // as having no org — otherwise the post-login fetch races the route guard and
  // bounces an org-having member to /onboarding (create-team).
  const [membershipsLoaded, setMembershipsLoaded] = useState(false)
  const [activeOrg, setActiveOrgState] = useState<Membership | null>(null)
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)
  // Tracks whose memberships are currently loaded. supabase-js re-emits SIGNED_IN on
  // EVERY tab focus (not only real logins); without this guard the onAuthStateChange
  // handler reset the ProtectedRoute gate on each re-emit, unmounting + remounting the
  // whole app — scrolling every open record back to the top on tab return. Keyed on the
  // user id, a re-emit for the SAME user is treated as a quiet refresh, not a sign-in.
  const loadedUserIdRef = useRef<string | null>(null)

  // Loads the caller's org memberships. Retries once on a transient failure: a
  // momentary network/RLS hiccup must never be mistaken for "this user has no
  // organizations", which would dead-end an org-having member on create-team.
  async function fetchMemberships(userId: string) {
    const run = () =>
      supabase
        .from('memberships')
        .select(`
          id,
          organization_id,
          role,
          is_default,
          organizations (id, name, slug, plan, brand)
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: true })

    let res = await run()
    if (res.error) {
      await new Promise(r => setTimeout(r, 800))
      res = await run()
    }

    if (res.error) {
      // Hard failure after the retry: do NOT collapse memberships to [] (that would
      // misroute the user to onboarding). Keep what we have; a reload retries the load.
      console.error('Failed to load memberships:', res.error.message)
      setMembershipsLoaded(true)
      return
    }

    const typed = (res.data ?? []) as unknown as Membership[]
    setMemberships(typed)
    // Last-chosen org (if still a membership) → default membership → oldest.
    const storedOrgId = readStoredOrgId(userId)
    const restored = storedOrgId ? typed.find(m => m.organization_id === storedOrgId) : undefined
    setActiveOrgState(restored ?? typed.find(m => m.is_default) ?? typed[0] ?? null)
    setMembershipsLoaded(true)
  }

  // Persisting setter — the sidebar org switcher calls this.
  function setActiveOrg(org: Membership) {
    setActiveOrgState(org)
    if (user) writeStoredOrgId(user.id, org.organization_id)
  }

  // Current-user profile (avatar + name) for the shared chrome. Refetching it
  // via refreshProfile() lets an avatar/name change on /profile update the
  // sidebar without a full reload.
  //
  // A FAILED fetch keeps the last-good profile and reports false — it never
  // writes null over what we have. postgrest-js auto-retries a failed GET and
  // then RESOLVES { data: null, error } rather than rejecting, and the old
  // data-only destructure turned that into setProfile(null): every consumer
  // deriving from preferences (contact board order, dashboard layout) then
  // re-derived against nothing and snapped to its default (G2 audit MAJOR E).
  async function fetchProfile(userId: string): Promise<boolean> {
    const { data, error } = await supabase
      .from('profiles')
      .select('avatar_url, full_name, first_name, last_name, preferences, timezone')
      .eq('id', userId)
      .maybeSingle()
    if (error) {
      console.error('Failed to load profile:', error.message)
      return false
    }
    setProfile((data as UserProfile) ?? null)
    return true
  }
  /** True when the profile state now reflects a fresh read. Callers holding an
   *  optimistic override keep it on false — the base they would fall back to is
   *  stale, and the override is what the DB actually holds. */
  async function refreshProfile(): Promise<boolean> {
    if (!user) return false
    return fetchProfile(user.id)
  }

  useEffect(() => {
    // C12: getSession() only DECODES the stored JWT (no signature check), so a tampered
    // /forged cached token would render as "logged in". Validate it SERVER-SIDE with
    // getUser() before trusting it. BUT a TRANSIENT validation failure (offline, 5xx,
    // or a request aborted when Chrome discards+reloads a backgrounded tab) must NOT be
    // treated like a forged token — the old code hard-signed-out on any failure, which
    // bounced valid users to /login. classifyValidation() splits a real rejection
    // (401/403 → forged/expired) from a transient one (keep the session).
    // `bootstrapDone` gates the listener below: supabase-js fires a cold-start event
    // storm (SIGNED_IN *and* INITIAL_SESSION) from the recovered, still-unvalidated
    // session, which would otherwise set user state and race past this validation.
    let bootstrapDone = false
    ;(async () => {
      const initial = await supabase.auth.getSession().catch(() => null)
      let s: Session | null = initial?.data.session ?? null

      if (s?.access_token) {
        // getUser() can return a null user + error, or throw on a network fault —
        // normalize both into { user, error } so the classifier sees a status.
        const validate = async (): Promise<{ user: User | null; error: { status?: number | null } | null }> => {
          try {
            const { data, error } = await supabase.auth.getUser()
            return { user: data.user, error }
          } catch (e) {
            return { user: null, error: (e as { status?: number }) ?? null }
          }
        }

        let res = await validate()
        let vu = res.user
        let verdict = classifyValidation(res.user, res.error)

        // A transient blip on the first request after a tab un-discards is common —
        // retry once after a short backoff before deciding anything.
        if (verdict === 'transient') {
          await new Promise((r) => setTimeout(r, 800))
          res = await validate()
          vu = res.user
          verdict = classifyValidation(res.user, res.error)
        }

        // Token genuinely rejected — but the refresh token may still be valid (an
        // expired access token). Trust it: refresh before giving up on the session.
        if (verdict === 'auth-invalid') {
          try {
            const refreshed = (await supabase.auth.refreshSession()).data.session
            if (refreshed?.user) { s = refreshed; vu = refreshed.user; verdict = 'valid' }
          } catch { /* refresh failed → definitively signed out */ }
        }

        if (verdict === 'valid' && vu) {
          setSession(s)
          setUser(vu)
          loadedUserIdRef.current = vu.id
          // Authenticate the Realtime socket with the live JWT explicitly (closes the
          // cold-start RLS-stream gap; idempotent with the library's connect-time setAuth).
          void supabase.realtime.setAuth(s.access_token)
          void fetchProfile(vu.id)
          fetchMemberships(vu.id).finally(() => setLoading(false))
          bootstrapDone = true
          return
        }

        if (verdict === 'transient') {
          // Couldn't reach the auth server to validate — do NOT sign out. Trust the
          // recovered session: the server still gates every data call (a forged token
          // 401s there and loads nothing), and autoRefresh keeps the token fresh. The
          // onAuthStateChange listener re-validates on the next token refresh.
          setSession(s)
          setUser(s.user)
          loadedUserIdRef.current = s.user.id
          void supabase.realtime.setAuth(s.access_token)
          void fetchProfile(s.user.id)
          fetchMemberships(s.user.id).finally(() => setLoading(false))
          bootstrapDone = true
          return
        }
        // verdict === 'auth-invalid' and refresh failed → forged/expired with no valid
        // refresh token → fall through to the signed-out purge below.
      }

      // No stored session, or a definitively-invalid token → signed out. Clear state +
      // loader (so ProtectedRoute resolves to /login), then purge the bad cached token.
      setSession(null); setUser(null); setLoading(false)
      loadedUserIdRef.current = null
      void supabase.realtime.setAuth()
      bootstrapDone = true
      void supabase.auth.signOut({ scope: 'local' }).catch(() => void 0)
    })()

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, s) => {
      // RIZ132: a sign-out from anywhere (another tab, a session that expired, the purge above) stops this
      // browser's push alerts. There is no session left to delete the device's row with, so the browser is
      // unsubscribed, and the push service answers the next alert 410, which drops the row. NEVER awaited:
      // supabase-js runs this callback inside its auth lock, and anything waited for here can hang
      // setSession() and updateUser() for good. On this tab's own sign-out, signOut() has already turned
      // alerts off and there is nothing left to unsubscribe, which settles at once. Ahead of the bootstrap
      // gate below, because a sign-out during the cold start is a sign-out too.
      if (event === 'SIGNED_OUT') {
        void forgetThisDevice().catch((err: unknown) => {
          console.error('auth: could not stop push alerts on this device after the sign-out:', err)
        })
      }
      // The cold-start recovery storm (SIGNED_IN + INITIAL_SESSION fired from the
      // still-unvalidated recovered session) is owned by the validated bootstrap above.
      // Ignore events until bootstrap completes so a forged session can't set user state
      // and race past the server-side getUser() validation (C12).
      if (!bootstrapDone) return
      setSession(s)
      setUser(s?.user ?? null)
      // Keep the Realtime socket authed across SIGNED_IN / TOKEN_REFRESHED /
      // USER_UPDATED with the fresh token, and clear it on SIGNED_OUT. Redundant
      // with the library listener on the happy path, but guarantees correctness
      // independent of library internals.
      if (s?.access_token) void supabase.realtime.setAuth(s.access_token)
      else void supabase.realtime.setAuth()
      if (s?.user) {
        // supabase-js re-emits SIGNED_IN every time the tab regains focus, not only on a
        // real login — so keying the gate reset on the EVENT tore the whole app down on
        // every tab return (ProtectedRoute swaps to its Loader while membershipsLoaded is
        // false, unmounting AppLayout + the current page, which then remount at the top of
        // a scrolled record). Key on the USER instead: only a genuinely new/changed user
        // resets the gate; a re-emit for the already-loaded user refetches quietly with no
        // loader flash — identical to how a token refresh was already handled.
        const isNewUser = s.user.id !== loadedUserIdRef.current
        loadedUserIdRef.current = s.user.id
        if (isNewUser) {
          // Real sign-in or a user switch: hold the gate until this user's memberships
          // resolve, so ProtectedRoute doesn't read the still-empty array as "no org" and
          // misroute an org-having member to /onboarding (create-team).
          setMembershipsLoaded(false)
        }
        void fetchProfile(s.user.id)
        fetchMemberships(s.user.id)
      } else {
        loadedUserIdRef.current = null
        setMemberships([])
        setActiveOrgState(null)
        setProfile(null)
        setMembershipsLoaded(false)
      }
    })

    // After the tab is backgrounded the socket drops and reconnects; no auth
    // event fires on that reconnect, so a long-idle tab can resubscribe before
    // the next token refresh. Re-assert the current token whenever the tab
    // becomes visible again so the resubscribed socket stays authed.
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void supabase.auth.getSession().then(({ data: { session: s } }) => {
          if (s?.access_token) void supabase.realtime.setAuth(s.access_token)
        })
      }
    }
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      subscription.unsubscribe()
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])

  async function signUp(
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    terms: { version: string; hash: string },
  ) {
    if (!terms?.version || !terms?.hash) {
      return { error: new Error('Please agree to the Terms of Service to continue.') }
    }
    const full_name = [firstName, lastName].map(s => s.trim()).filter(Boolean).join(' ')
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          full_name,
          // Snapshotted by public.record_signup_terms_acceptance() into
          // terms_acceptances — that row is the Section 3.3 clickwrap log.
          accepted_terms_version: terms.version,
          accepted_terms_hash: terms.hash,
          // RIZ183: handle_new_user (mig 479) starts the profile in this zone when it is
          // a real one, instead of the column's America/Boise.
          timezone: deviceTimeZone() ?? undefined,
        },
      },
    })
    return { error: error as Error | null }
  }

  async function signIn(email: string, password: string) {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password })
    return { data, error: error as Error | null }
  }

  async function signOut() {
    // RIZ132: this device's push alerts go off FIRST, while there is still a session to delete its row
    // with, so a phone or an office computer left signed out does not keep putting this person's texts
    // on its lock screen. Limited and caught: the sign-out below goes ahead whatever happens here, and
    // within SIGN_OUT_ALERTS_LIMIT_MS.
    let timer: ReturnType<typeof setTimeout> | undefined
    try {
      const off = await Promise.race([
        disableAlerts().then(() => true),
        new Promise<false>((resolve) => { timer = setTimeout(() => resolve(false), SIGN_OUT_ALERTS_LIMIT_MS) }),
      ])
      if (!off) console.warn('signOut: push alerts on this device did not finish turning off in time; signing out anyway')
    } catch (err) {
      console.error('signOut: could not turn push alerts off on this device; signing out anyway:', err)
    } finally {
      clearTimeout(timer)
    }
    // 'local', not the default 'global': a global sign-out ends every session of the
    // user, so on a shared login (the Cedar Ridge demo) it would sign everyone out.
    await supabase.auth.signOut({ scope: 'local' })
    setMemberships([])
    setActiveOrgState(null)
    setProfile(null)
    setMembershipsLoaded(false)
  }

  return (
    <AuthContext.Provider value={{
      user, session, memberships, membershipsLoaded, activeOrg, setActiveOrg,
      profile, refreshProfile, loading, signUp, signIn, signOut,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

// I approve bandaid: provider + hook colocation is intentional (50 importers);
// splitting the module for the Fast Refresh DX rule isn't worth the churn.
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
